import { cloneDeep } from 'lodash';
import { isWebpImage } from '@meitu/saas-editor-core/es/utils/file-kit';
import { traverseTrees } from '@meitu/saas-editor-core/es/utils/tree-kit';
import { IServerLayerProps, parseServerLayersToClient } from '@meitu/saas-editor-core/es/version-parse';
import { CanvasType, ILayerProps, LayerType, MediaType } from '@meitu/saas-editor-core/es/version-types';
import { getElementTypeAndExtObj } from '@meitu/xdesign-widgets';
import { useAddImageLayer } from '@/editor/collapse/views/material-collapse/hooks/add-image';
import { useAddMediaLayer } from '@/editor/collapse/views/material-collapse/hooks/add-media';
import { useAddVectorLayer } from '@/editor/collapse/views/material-collapse/hooks/add-vector';
import { addToStage } from '@/hooks/use-operate-my-space';
import { editor } from '@/instance';
import { useTemplateModel } from '@/store/models/template-model';
import { beforeParseServerLayersToClient } from './fetch-template/server-to-client';
import { parseClientToServerLayers } from './save-template/client-to-server';

const transformMaterialId = (layer: IServerLayerProps) => {
    const parseLayer = (layer: IServerLayerProps) => {
        if (!layer?.materialIds) {
            layer.materialIds = {};
        } else if (layer?.materialIds instanceof Map) {
            layer.materialIds = Object.fromEntries(layer.materialIds);
        }
    };
    parseLayer(layer);
    traverseTrees(layer.children, (childLayer) => {
        parseLayer(childLayer);
    });
};

const useAddUploadLayer = () => {
    const addVectorLayer = useAddVectorLayer();
    const addMediaLayer = useAddMediaLayer();
    const { add: addImageLayer } = useAddImageLayer();
    const resolution = useTemplateModel((state) => state.resolution);

    type OldLayer = Omit<ILayerProps, 'materialIds'> & {
        materialIds: Record<string, any>;
    };
    type NewLayer = IServerLayerProps & { version: string };
    /** 兼容我的空间的图层元素的旧数据: 旧数据不符合服务端规范或新数据, 旧数据是materialIds为对象的ILayerProps, 新数据是带version的IServerLayerProps */
    const getClientLayer = async (LayerExtObj: OldLayer | NewLayer): Promise<ILayerProps> => {
        const { width: stageWidth, height: stageHeight } = editor.services.frame.getSize();
        const unit = editor.services.editor.getUnit();
        const stageType = editor.services.editor.getCanvasType();
        const originLayer = cloneDeep(LayerExtObj);
        const checkIsOld = (layer: any): layer is OldLayer => !!layer.data;
        const isOld = checkIsOld(originLayer);
        console.log('===layerIsOld: ', isOld);
        let layer: ILayerProps;
        if (isOld) {
            const { layers, version } = parseClientToServerLayers(
                [originLayer as ILayerProps],
                stageType === CanvasType.VARIETY ||
                    (stageType === CanvasType.SINGLE && editor.services.frame.getFrameIdsLength() > 1),
                { unit, resolution },
            );
            // parseClientToServerLayers里面没有转materialIds, 需要手动转换, 否则走到parseServerLayersToClient里面会导致materialIds转换失败导致丢失
            transformMaterialId(layers[0]);
            beforeParseServerLayersToClient(layers);
            layer = parseServerLayersToClient({ layers, width: stageWidth, height: stageHeight }, version)[0];
        } else {
            beforeParseServerLayersToClient([originLayer as IServerLayerProps], false);
            layer = parseServerLayersToClient(
                {
                    layers: [originLayer as IServerLayerProps],
                    width: stageWidth,
                    height: stageHeight,
                },
                originLayer.version,
            )[0];
        }
        return layer;
    };

    const handleAddUploadLayer = async ({
        image,
        isPhoto = false,
        hasTeamFontBiz = false,
        byMousePosition = false,
        fixedCenter = false,
        fitWithStage = true,
    }: {
        image: {
            url: string;
            width: number;
            height: number;
            /** 元素的自定义存储数据 */
            ext?: string;
            /** 素材id，用于判断是否需要vip */
            id?: string;
        };
        isPhoto?: boolean;
        hasTeamFontBiz?: boolean;
        trackFunc?: string;
        byMousePosition?: boolean;
        fixedCenter?: boolean;
        fitWithStage?: boolean;
    }) => {
        const { elementType, extObj } = getElementTypeAndExtObj(image.ext);

        if (isWebpImage(image.url)) {
            //如果上传是media图层
            const data = {
                id: '',
                url: image.url,
                info: {
                    naturalWidth: image.width,
                    naturalHeight: image.height,
                    mediaType: MediaType.Image,
                },
                byMousePosition,
            };
            addMediaLayer.add(data);
            return;
        }
        if (elementType === 'common') {
            await addImageLayer({
                id: image.id ?? '',
                url: image.url,
                isPhoto,
                width: image.width,
                height: image.height,
                showLoading: true,
                byMousePosition,
                fixedCenter,
                fitWithStage,
            });
            return;
        }

        if (elementType === 'svg') {
            await addVectorLayer.add({
                id: 0,
                image: extObj.image,
                info: JSON.stringify(extObj.info),
                byMousePosition,
                // fitWithStage
            });
            return;
        }

        if (elementType === 'media') {
            await addMediaLayer.add({
                id: '',
                url: image.url,
                info: {
                    naturalWidth: image.width,
                    naturalHeight: image.height,
                    mediaType: extObj.mediaType,
                },
                byMousePosition,
                // fitWithStage
            });
            return;
        }

        if (elementType === 'layer') {
            console.log('===layerExtObj', extObj);
            const clientLayer = await getClientLayer(extObj as OldLayer | NewLayer);
            console.log('===clientLayer', cloneDeep(clientLayer));
            if (clientLayer.layerType === LayerType.WATERMARK) {
                await addImageLayer({
                    id: '',
                    url: image.url,
                    isPhoto: false,
                    width: clientLayer.width,
                    height: clientLayer.height,
                    showLoading: true,
                    byMousePosition,
                    fixedCenter,
                    fitWithStage,
                });
                return;
            }

            await addToStage(clientLayer, fitWithStage, fixedCenter, byMousePosition);
        }
    };
    return { handleAddUploadLayer };
};

export default useAddUploadLayer;
